/**
 * Vault Backfill Script
 * 
 * Run this to enrich all existing items in the vault with:
 * - Embeddings
 * - Themes
 * - Topics
 * - Normalized structure
 * 
 * Usage:
 * 1. Open browser console
 * 2. Run: window.backfillVault()
 * 3. Or: window.backfillVault('user-id', { limit: 100 })
 */

import vaultWriterService from '../services/vaultWriterService';
import vaultService from '../services/vaultService';
import { supabase } from '../lib/supabase';

/**
 * Main backfill function
 */
export async function runBackfill(userId = null, options = {}) {
  const {
    limit = 50,
    skipEmbedding = false,
    dryRun = false,
  } = options;

  try {
    // Get user ID if not provided
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }

    if (!userId) {
      throw new Error('No user ID provided and not logged in');
    }

    if (import.meta.env.DEV) {
      console.log('🔄 Starting Vault Backfill');
      console.log('   User:', userId.slice(0, 8));
      console.log('   Limit:', limit);
      console.log('   Skip Embedding:', skipEmbedding);
      console.log('   Dry Run:', dryRun);
      console.log('');
    }

    // Step 1: Get all items
    const allItems = await vaultService.getAllItems(userId, { forceRefresh: true });
    if (import.meta.env.DEV) console.log('📊 Total items in vault:', allItems.length);

    // Step 2: Check which need enrichment
    const needsEnrichment = allItems.filter(item => {
      const validation = vaultWriterService.validateEnrichment(item);
      return !validation.valid;
    });

    if (import.meta.env.DEV) console.log('📊 Items needing enrichment:', needsEnrichment.length);

    if (needsEnrichment.length === 0) {
      if (import.meta.env.DEV) console.log('✅ All items are fully enriched!');
      return { enriched: 0, skipped: 0, errors: [] };
    }

    // Show breakdown
    const missingFields = {
      embedding: 0,
      themes: 0,
      topics: 0,
    };

    needsEnrichment.forEach(item => {
      if (!item.embedding) missingFields.embedding++;
      if (!item.extractedThemes || item.extractedThemes.length === 0) missingFields.themes++;
      if (!item.microTopics || item.microTopics.length === 0) missingFields.topics++;
    });

    if (import.meta.env.DEV) {
      console.log('📊 Missing fields breakdown:');
      console.log('   Embeddings:', missingFields.embedding);
      console.log('   Themes:', missingFields.themes);
      console.log('   Topics:', missingFields.topics);
      console.log('');
    }

    if (dryRun) {
      if (import.meta.env.DEV) console.log('🔍 DRY RUN - No changes made');
      return {
        dryRun: true,
        wouldEnrich: Math.min(needsEnrichment.length, limit),
        total: needsEnrichment.length,
      };
    }

    // Step 3: Backfill
    if (import.meta.env.DEV) console.log(`🚀 Enriching ${Math.min(needsEnrichment.length, limit)} items...`);
    
    const result = await vaultWriterService.backfillMissingFields(userId, {
      limit,
      skipEmbedding,
    });

    if (import.meta.env.DEV) {
      console.log('');
      console.log('✅ BACKFILL COMPLETE');
      console.log('   Enriched:', result.enriched);
      console.log('   Errors:', result.errors.length);
      console.log('   Remaining:', result.remaining);

      if (result.errors.length > 0) {
        console.log('');
        console.log('❌ Errors:', result.errors);
      }
    }

    return result;

  } catch (error) {
    console.error('❌ Backfill failed:', error);
    throw error;
  }
}

/**
 * Validate vault integrity
 */
export async function validateVault(userId = null) {
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    userId = user?.id;
  }

  if (!userId) {
    throw new Error('No user ID');
  }

  if (import.meta.env.DEV) console.log('🔍 Validating vault integrity...');
  const result = await vaultWriterService.validateVaultIntegrity(userId);

  if (import.meta.env.DEV) {
    if (result.valid) {
      console.log('✅ Vault is valid - all', result.itemsChecked, 'items enriched');
    } else {
      console.log('❌ Found', result.invalidItems.length, 'items with issues');
      console.log('Run window.backfillVault() to fix');
    }
  }

  return result;
}

// Expose globally
if (typeof window !== 'undefined') {
  window.backfillVault = runBackfill;
  window.validateVault = validateVault;
  if (import.meta.env.DEV) {
    console.log('🔧 Vault backfill commands loaded:');
    console.log('   window.backfillVault() - Enrich all items');
    console.log('   window.validateVault() - Check vault integrity');
  }
}

export default {
  runBackfill,
  validateVault,
};

